/**
 * useResponsive Hook
 * 
 * Provides responsive breakpoint detection for mobile-friendly layouts.
 * Returns boolean flags for mobile, tablet, and desktop screen sizes.
 */

import { useState, useEffect, useCallback } from 'react';

export interface ResponsiveState {
  isMobile: boolean;      // < 768px
  isTablet: boolean;      // 768px - 1024px
  isDesktop: boolean;     // > 1024px
  width: number;
}

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

export function useResponsive(): ResponsiveState {
  const getResponsiveState = useCallback((): ResponsiveState => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1200;
    return {
      isMobile: width < MOBILE_BREAKPOINT,
      isTablet: width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT,
      isDesktop: width >= TABLET_BREAKPOINT,
      width,
    };
  }, []);

  const [state, setState] = useState<ResponsiveState>(getResponsiveState);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      // Debounce resize events for performance
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Only re-render when a BREAKPOINT actually flips — dragging the window
        // within one breakpoint used to churn `width` and re-render every card
        // that reads useResponsive (#153).
        setState((prev) => {
          const next = getResponsiveState();
          if (
            prev.isMobile === next.isMobile &&
            prev.isTablet === next.isTablet &&
            prev.isDesktop === next.isDesktop
          ) {
            return prev;
          }
          return next;
        });
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    
    // Set initial state
    setState(getResponsiveState());

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [getResponsiveState]);

  return state;
}

export default useResponsive;
